import { Typography } from "@mui/material";
import "./styles.css";
import Latex from "react-latex-next";
import NextAction from "../../utils/NextAction";

export default function Controls({ controls }) {
  const {} = controls;
  return (
    <div className="controls">
      <div className="controlGroup">
        <Typography variant="h5" style={{ paddingBottom: 20 }}>
          Lagrange Dynamics Equation
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          The <i>Lagrangian</i> of the mechanical system can be defined as a
          function : <Latex>$$L = T - U$$</Latex>
          where <Latex>$T$ and $U$</Latex> respectively denote the total{" "}
          <i>kinetic energy</i> and <i>potential energy</i> of the system.
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          The Lagrange equations are expressed by :{" "}
          <Latex>{`$$ \\frac{d}{dt}\\frac{\\partial L}{\\partial \\dot{q}_i} - \\frac{\\partial L}{\\partial q_i} = \\xi_i$$`}</Latex>
          where <Latex>$\xi_i$</Latex> is the
          <i>generalized force</i> associated with the generalized coordinate{" "}
          <Latex>$q_i$</Latex>. (note: gravity is not in <Latex>$\xi_i$</Latex>)
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          We use the right simple case of one pendulum for example. Let{" "}
          <Latex>$\theta$</Latex> denote the angle with respect to the reference
          position of the body hanging down (<Latex>$\theta$ = 0</Latex>). By
          choosing <Latex>$\theta$</Latex> as the generalized coordinate and
          assume there are no torque from rovolute joint at origin, the Lagrange
          equations are expressed by
          <Latex>{`$$ \\frac{d}{dt}\\frac{\\partial L}{\\partial \\dot{\\theta}_i} - \\frac{\\partial L}{\\partial \\theta_i} = 0$$`}</Latex>
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          The kinetic energy <Latex>$T$</Latex> is
          <Latex>{`$$ T = \\frac{1}{2}mv^2 = \\frac{1}{2}ml^2\\dot{\\theta}^2$$`}</Latex>
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          The potential energy <Latex>$U$</Latex> is
          <Latex>{`$$ U = mgl(1-cos\\theta) $$`}</Latex>
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          The <i>Lagrangian</i> is
          <Latex>{`$$ L = \\frac{1}{2}ml^2\\dot{\\theta}^2 - mgl(1-cos\\theta) $$`}</Latex>
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          So,
          <Latex>{`$$ \\frac{\\partial L}{\\partial \\dot{\\theta}} = m \\cdot l^2 \\cdot \\dot{\\theta}$$`}</Latex>
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Continuing,
          <Latex>{`$$ \\frac{d}{dt}\\frac{\\partial L}{\\partial \\dot{\\theta}} =  \\cdot l^2 \\cdot \\ddot{\\theta}$$`}</Latex>
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Then,
          <Latex>{`$$ \\frac{\\partial L}{\\partial \\theta} = - m \\cdot g \\cdot l \\cdot sin \\theta$$`}</Latex>
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Now, putting these last two equations together
          <Latex>{`$$ \\frac{d}{dt}\\frac{\\partial L}{\\partial \\dot{q}_i} - \\frac{\\partial L}{\\partial q_i} = m \\cdot l^2 \\cdot \\ddot{\\theta} + m \\cdot g \\cdot l \\cdot sin \\theta$$`}</Latex>
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          Simplifying,
          <Latex>{`$$ \\ddot{\\theta} + \\frac{g}{l} \\cdot sin \\theta = 0$$`}</Latex>
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          To simulate the equation in computer system, you might be thinking
          that we can find the next state of the pendulum system by taking a
          small change in time (<Latex>$\Delta t$</Latex>), multiplying the
          derivatives of the state values by , and adding the resulting changes
          to the current state values. The method is called Eulers Method.
          <Latex>{`$$ \\left( \\begin{array}
          {c} \\theta \\\\ \\dot{\\theta} \\end{array}
          \\right) \\rightarrow \\left( \\begin{array}
            {c} \\theta \\\\ \\dot{\\theta} \\end{array}
            \\right) + \\Delta t \\left( \\begin{array}
              {c} \\dot{\\theta} \\\\ \\ddot{\\theta} \\end{array}
              \\right) $$`}</Latex>
          (Actually, we do not use this simple method; we use the RK4 method
          instead. I write a short{" "}
          <a href="https://medium.com/@visro/euler-vs-runge-kutta-methods-de34565bf0cf">
            blog
          </a>{" "}
          to explain it.{" "}
          <a target="_blank" href="/robotics/numerical-methods-for-ode">
            Here
          </a>{" "}
          is the visualization of Euler Method and RK4 Method. You can also
          select the ODE solvers as Euler and see the strange result.)
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          If we know the initial{" "}
          <Latex>
            {`$\\dot{\\theta}$`} and {`$\\theta$`}
          </Latex>
          , then we can use{" "}
          <Latex>
            {`$$\\ddot{\\theta} = - \\frac{g}{l} \\cdot sin \\theta$$`}
          </Latex>
          to estimate the next state{" "}
          <Latex>
            {`$\\dot{\\theta}$`} and {`$\\theta$`}
          </Latex>
          . The right is the implementation of these equations.
        </Typography>
        <Typography style={{ paddingBottom: 10, textAlign: "left" }}>
          If we try to add friction <Latex>$F$</Latex> and joint torque{" "}
          <Latex>$\tau$</Latex> at the original point, thing becomes interested.
          <br />
          The Lagrange equations are expressed by :
          <Latex>{`$$ \\frac{d}{dt}\\frac{\\partial L}{\\partial \\dot{q}_i} - \\frac{\\partial L}{\\partial q_i} = \\tau - F\\frac{\\dot{\\theta}}{|\\dot{\\theta}|}$$`}</Latex>
          Then,
          <Latex>{`$$ m \\cdot l^2 \\cdot \\ddot{\\theta} + m \\cdot g \\cdot l \\cdot sin \\theta = \\tau - F\\frac{\\dot{\\theta}}{|\\dot{\\theta}|}$$`}</Latex>
          To simplify the simulation, we assume <Latex>$F$</Latex> and{" "}
          <Latex>$\tau$</Latex> are constant here. You can change the value at
          the right side and see the result. (<Latex>$F$</Latex> and{" "}
          <Latex>$\tau$</Latex> are positive when anti clock-wise.)
        </Typography>
        <NextAction label={"Next: PID Control with Cart-Pole"} to="/pid" />
      </div>
    </div>
  );
}
